import React, { useEffect, useRef } from 'react';
import './AppsPrivacy.css';
import NavBar3 from '../../components/NavBar3/NavBar3';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { motion } from 'framer-motion';

const AppsPrivacy = () => {
  const sphereRef = useRef(null);
  const scrollPos = useRef(0);
  const spherePos = useRef(-50);

  useEffect(() => {
    const sphere = sphereRef.current;
    if (sphere) {
      sphere.classList.add('animate-in');

      const handleScroll = () => {
        const currentScroll = window.scrollY;
        const scrollDirection = currentScroll > scrollPos.current ? 'down' : 'up';
        scrollPos.current = currentScroll;

        if (scrollDirection === 'down') {
          spherePos.current = Math.min(0, spherePos.current + 0.5);
        } else {
          spherePos.current = Math.max(-100, spherePos.current - 0.5);
        }

        sphere.style.transform = `translateY(${spherePos.current}%)`;
      };

      let ticking = false;
      const optimizedScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', optimizedScroll);
      return () => window.removeEventListener('scroll', optimizedScroll);
    }
  }, []);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="apps-privacy-page">
      <div className="background-sphere" id="background-sphere" ref={sphereRef}></div>
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          background: { color: { value: "transparent" } },
          fpsLimit: 120,
          interactivity: {
            events: {
              onClick: { enable: true, mode: "push" },
              onHover: { enable: true, mode: "repulse" }
            },
            modes: {
              push: { quantity: 4 },
              repulse: { distance: 100, duration: 0.4 }
            }
          },
          particles: {
            color: { value: ["#FF9000", "#FFD700", "#FFFFFF"] },
            links: {
              color: "#FF9000",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1
            },
            move: {
              direction: "none",
              enable: true,
              outModes: { default: "bounce" },
              random: false,
              speed: 1,
              straight: false
            },
            number: { density: { enable: true }, value: 80 },
            opacity: { value: { min: 0.1, max: 0.5 } },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 3 } }
          },
          detectRetina: true
        }}
      />

      <NavBar3 />

      <motion.div
        className="apps-privacy-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="apps-privacy-content" variants={itemVariants}>
          <motion.h1 variants={itemVariants}>Apps Privacy Policy</motion.h1>
          <motion.p className="last-updated" variants={itemVariants}>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>

          <motion.p className="intro-text" variants={itemVariants}>
            This Privacy Policy applies to all mobile, desktop, and web applications (collectively referred to as "the Applications" or "our Apps") developed, published, and maintained by <strong>Skript</strong> ("we", "us", or "our"). 
          </motion.p>

          <motion.section variants={itemVariants}>
            <h2>1. Introduction</h2>
            <p>
              We are committed to protecting your privacy and ensuring you have a positive experience when using our Apps. This Privacy Policy describes how we collect, use, store, and share information when you download, install, access, or use any App developed by Skript.
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>2. Information We Collect</h2>
            <p>Depending on the App you are using, we may collect different types of information to provide and improve the service:</p>
            
            <h3>2.1 Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Registration:</strong> If an App requires signup, we may collect your name, email address, username, and password.</li>
              <li><strong>User Content:</strong> Any photos, text, files, or media that you explicitly choose to upload, input, or share within the App.</li>
              <li><strong>Support Communications:</strong> Information you send us when requesting customer support or providing feedback.</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Device & OS Information:</strong> Device model, operating system version, unique device identifiers, and network information.</li>
              <li><strong>Usage Metrics:</strong> Features accessed, time spent, screens viewed, and navigation patterns within the App to help us understand user engagement.</li>
              <li><strong>Diagnostics & Crash Logs:</strong> Performance data, crash reports, and error logs (e.g., via tools like Firebase Crashlytics) to debug issues and maintain application stability.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>3. Device Permissions</h2>
            <p>
              Our Apps may request access to certain features of your device to function correctly. These permissions will only be requested when necessary, and you can grant or revoke them at any time through your device settings:
            </p>
            <ul>
              <li><strong>Camera & Photo Library:</strong> To allow you to take photos or upload media within the App.</li>
              <li><strong>Storage:</strong> To save or access files directly on your device.</li>
              <li><strong>Notifications:</strong> To send you push notifications regarding updates, alerts, or App activity.</li>
              <li><strong>Network State & Wi-Fi:</strong> To check internet connectivity for online features.</li>
              <li><strong>Location:</strong> To provide location-based features (only if explicitly requested and approved).</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>4. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>To operate, maintain, support, and optimize the features of our Apps.</li>
              <li>To troubleshoot bugs, prevent crashes, and enhance performance.</li>
              <li>To communicate with you regarding App updates, security alerts, and support requests.</li>
              <li>To analyze aggregate user behavior to make informed design and product decisions.</li>
              <li>To enforce our terms and comply with legal requirements.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>5. Data Storage and Security</h2>
            <p>
              Your data security is extremely important to us. We implement industry-standard administrative, technical, and physical security measures to safeguard your information from unauthorized access, loss, misuse, or alteration.
            </p>
            <p>
              Depending on the App, data may be stored locally on your device or securely in the cloud (using trusted providers such as Firebase/Google Cloud Platform).
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>6. Sharing and Third-Party Services</h2>
            <p>
              We do not sell, rent, or trade your personal information. We may share information with trusted third-party service providers that assist us in operating our Apps:
            </p>
            <ul>
              <li><strong>Analytics Providers:</strong> Services like Google Analytics for Firebase to analyze usage patterns.</li>
              <li><strong>Crash Reporting:</strong> Services like Firebase Crashlytics to monitor App stability.</li>
              <li><strong>Cloud Storage & Hosting:</strong> Reliable databases and storage solutions to sync App data.</li>
            </ul>
            <p>
              All third-party partners are required to protect your data under their respective privacy policies and comply with applicable laws.
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>7. Children's Privacy</h2>
            <p>
              Our Apps are not intended for children under the age of 13 (or the applicable age in your jurisdiction). We do not knowingly collect personal information from children. If we discover that a child has provided us with personal information, we will delete it immediately.
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>8. Your Rights and Choices</h2>
            <p>
              You have control over your data. Depending on your location and the specific App, you may have the right to access, update, correct, or request deletion of the personal data we hold about you. You can do so by contacting us directly.
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>9. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. Any updates will be posted on this page with the updated revision date.
            </p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>10. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our Apps, please contact us at:</p>
            <div className="contact-details">
              <p><strong>Skript Support Team</strong></p>
              <p>Email: <a href="mailto:support@skriptdev.com">support@skriptdev.com</a></p>
              <p>Website: <a href="https://skriptdev.com" target="_blank" rel="noopener noreferrer">skriptdev.com</a></p>
            </div>
          </motion.section>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AppsPrivacy;
